import { Component, signal, inject, ChangeDetectionStrategy, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { DepartmentSchemesService } from '../../services/department-schemes.service';
import { UserService } from '../../services/user.service';
import { DashboardStatsService } from '../../services/dashboard-stats.service';
import { FieldExecutionStatsService } from '../../services/field-execution-stats.service';
import { environment } from '../../../environments/environment';
import { computed } from '@angular/core';

@Component({
  selector: 'app-login',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private deptService = inject(DepartmentSchemesService);
  private dashboardStats = inject(DashboardStatsService);
  private fieldExecutionStats = inject(FieldExecutionStatsService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private userService = inject(UserService);

  loginForm: FormGroup;
  isLoading = signal(false);
  errorMessage = signal<string | null>(null);
  successMessage = signal<string | null>(null);
  showPassword = signal(false);
  isDev = !environment.production;
  lastLoginDate = signal(new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) + ', ' + new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }));
  
  // Live portfolio count — HM Decision + Field Execution (updates as Firestore changes).
  totalProjectsCount = computed(() => {
    const hmLive = this.dashboardStats.isLive();
    const feLive = this.fieldExecutionStats.isLive();

    if (hmLive || feLive) {
      const hm = hmLive ? this.dashboardStats.projects().length : 0;
      const fe = feLive ? this.fieldExecutionStats.totalProjects() : 0;
      return hm + fe;
    }

    return this.deptService.getDepartments().reduce(
      (sum, d) => sum + d.schemes.reduce((s, sc) => s + sc.projects, 0),
      0
    );
  });

  projectsCountLabel = computed(() =>
    this.totalProjectsCount().toLocaleString('en-IN')
  );

  totalDepartmentsCount = computed(() => this.deptService.getDepartments().length);

  ngOnInit(): void {
    document.body.classList.add('login-active');
  }

  ngOnDestroy(): void {
    document.body.classList.remove('login-active');
  }

  constructor() {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      rememberMe: [false]
    });
  }

  togglePasswordVisibility(): void {
    this.showPassword.update(value => !value);
  }

  async onSubmit(): Promise<void> {
    if (this.loginForm.invalid) {
      Object.keys(this.loginForm.controls).forEach(key => {
        this.loginForm.get(key)?.markAsTouched();
      });
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);

    const { email, password } = this.loginForm.value;

    try {
      console.log('Login component: Starting login process...');
      await this.authService.login(email, password);
      console.log('Login component: Login successful!');
      // Navigation is handled by auth service, no need to navigate again
    } catch (error: any) {
      console.error('Login component: Login failed:', error);
      this.errorMessage.set(this.authService.errorMessage());
    } finally {
      console.log('Login component: Resetting loading state');
      this.isLoading.set(false);
    }
  }

  async forgotPassword(): Promise<void> {
    this.errorMessage.set(null);
    this.successMessage.set(null);
    const email = this.loginForm.get('email')?.value;

    if (!email) {
      this.errorMessage.set('Please enter your email address first to reset password.');
      return;
    }

    this.isLoading.set(true);
    try {
      await this.authService.resetPassword(email);
      this.successMessage.set('Password reset email sent! Please check your inbox.');
      setTimeout(() => this.successMessage.set(null), 5000);
    } catch (err: any) {
      this.errorMessage.set('Failed to send reset email: ' + (err.message || 'Unknown error'));
    } finally {
      this.isLoading.set(false);
    }
  }

  getFieldError(fieldName: string): string {
    const field = this.loginForm.get(fieldName);

    if (field?.hasError('required')) {
      return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required`;
    }

    if (field?.hasError('email')) {
      return 'Please enter a valid email address';
    }

    if (field?.hasError('minlength')) {
      return 'Password must be at least 6 characters';
    }

    return '';
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.loginForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  async setupAccounts(): Promise<void> {
    this.isLoading.set(true);
    this.errorMessage.set(null);
    this.successMessage.set('Setting up test accounts…');

    try {
      const result = await this.userService.seedMasterAccounts();
      if (result.failed.length > 0) {
        throw new Error(`Failed for: ${result.failed.join(', ')}`);
      }

      this.loginForm.patchValue({
        email: 'admin@email.com',
        password: '123456',
      });

      this.successMessage.set('Accounts ready. Signing in as Admin…');
      await this.authService.login('admin@email.com', '123456');
    } catch (error: any) {
      console.error('Setup failed:', error);
      this.errorMessage.set(error?.message || 'Setup failed. Check console and Firebase connectivity.');
      this.successMessage.set(null);
      this.isLoading.set(false);
    }
  }
}
